import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { UsersRound, Filter, ChevronDown, ChevronUp, Download, FileText, RefreshCw, GripVertical, AlertTriangle, Wand2, Save, History, Trash2, RotateCcw, Route, FileDown } from 'lucide-react';
import ChildAvatar from '@/components/ChildAvatar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/contexts/AuthContext';
import { addPdfHeader, getPdfHeaderHeight } from '@/utils/pdfHelper';

const Groups = () => {
  const { canEdit, isAdmin } = useAuth();
  const canEditGroups = canEdit('groups') || isAdmin();
  
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTurmas, setExpandedTurmas] = useState({});
  
  // Slot info para cada criança (para mostrar indicador de slot vazio)
  const [childrenSlotInfo, setChildrenSlotInfo] = useState({});
  
  // Modal de grade da criança
  const [selectedChild, setSelectedChild] = useState(null);
  const [childSchedule, setChildSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  
  // Modal de opções de slot
  const [slotOptions, setSlotOptions] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotOptionsLoading, setSlotOptionsLoading] = useState(false);
  
  // Drag & Drop
  const [draggedChild, setDraggedChild] = useState(null);
  const [dragSourceTurma, setDragSourceTurma] = useState(null);
  const [dropTargetTurma, setDropTargetTurma] = useState(null);
  
  // Confirmação de exceder limite
  const [confirmExceedDialog, setConfirmExceedDialog] = useState(null);
  
  // Geração de grades
  const [generatingGrades, setGeneratingGrades] = useState(false);
  const [gradeResultDialog, setGradeResultDialog] = useState(null);
  const [syncingActivities, setSyncingActivities] = useState(false);
  
  // Controle de versões (Snapshots)
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsDialogOpen, setSnapshotsDialogOpen] = useState(false);
  const [saveSnapshotDialogOpen, setSaveSnapshotDialogOpen] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotDescription, setSnapshotDescription] = useState('');
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [restoringSnapshot, setRestoringSnapshot] = useState(false);
  const [confirmRestoreDialog, setConfirmRestoreDialog] = useState(null);
  
  // Modal de trilha
  const [trilhaModalOpen, setTrilhaModalOpen] = useState(false);
  const [trilhaData, setTrilhaData] = useState(null);
  const [trilhaLoading, setTrilhaLoading] = useState(false);
  
  // Transferência de grupo
  const [transferringTurma, setTransferringTurma] = useState(null); // turmaKey sendo transferida
  
  // Filtros
  const [filters, setFilters] = useState({
    activity: '',
    day: '',
    shift: '',
    timeSlot: ''
  });
  
  const [activities, setActivities] = useState([]);

  const daysOfWeek = [
    { value: 'segunda', label: 'Segunda' },
    { value: 'terca', label: 'Terça' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'quinta', label: 'Quinta' },
    { value: 'sexta', label: 'Sexta' }
  ];

  const timeSlots = [
    { value: 1, label: '1° Horário' },
    { value: 2, label: '2° Horário' },
    { value: 3, label: '3° Horário' }
  ];

  useEffect(() => {
    fetchTurmas();
    fetchActivities();
  }, []);

  // Fechar dropdown de transferência ao clicar fora
  useEffect(() => {
    if (!transferringTurma) return;
    const handleClickOutside = () => setTransferringTurma(null);
    const timer = setTimeout(() => document.addEventListener('click', handleClickOutside), 100);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClickOutside); };
  }, [transferringTurma]);

  const fetchTurmas = async () => {
    try {
      const response = await axios.get(`${API}/turmas`);
      setTurmas(response.data);
      
      const expanded = {};
      response.data.forEach((turma) => {
        expanded[`${turma.activity_id}_${turma.day}_${turma.shift}_${turma.time_slot}`] = true;
      });
      setExpandedTurmas(expanded);
    } catch (error) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
    // Buscar slot info separadamente (não bloqueia turmas)
    fetchChildrenSlotInfo();
  };

  const fetchChildrenSlotInfo = async () => {
    try {
      const res = await axios.get(`${API}/slot-info-batch`);
      setChildrenSlotInfo(res.data || {});
    } catch (error) {
      console.log('Slot info batch não disponível');
    }
  };

  // Atualizar slot info (recarrega batch)
  const updateChildSlotInfo = async () => {
    await fetchChildrenSlotInfo();
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data.filter(a => a.type !== 'outro'));
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
    }
  };

  // ==================== FUNÇÕES DE SNAPSHOT ====================
  
  const fetchSnapshots = async () => {
    try {
      const response = await axios.get(`${API}/groups/snapshots`);
      setSnapshots(response.data);
    } catch (error) {
      console.error('Erro ao buscar snapshots:', error);
    }
  };
  
  const handleSaveSnapshot = async () => {
    if (!snapshotName.trim()) {
      toast.error('Digite um nome para o snapshot');
      return;
    }
    
    setSavingSnapshot(true);
    try {
      const response = await axios.post(`${API}/groups/snapshots`, {
        name: snapshotName.trim(),
        description: snapshotDescription.trim()
      });
      
      toast.success(`Snapshot "${snapshotName}" salvo com sucesso!`);
      setSaveSnapshotDialogOpen(false);
      setSnapshotName('');
      setSnapshotDescription('');
      fetchSnapshots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar snapshot');
    } finally {
      setSavingSnapshot(false);
    }
  };
  
  const handleRestoreSnapshot = async (snapshot) => {
    setRestoringSnapshot(true);
    try {
      const response = await axios.post(`${API}/groups/snapshots/${snapshot.id}/restore`);
      
      toast.success(`Snapshot "${snapshot.name}" restaurado com sucesso!`);
      setConfirmRestoreDialog(null);
      setSnapshotsDialogOpen(false);
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar snapshot');
    } finally {
      setRestoringSnapshot(false);
    }
  };
  
  const handleDeleteSnapshot = async (snapshotId) => {
    try {
      await axios.delete(`${API}/groups/snapshots/${snapshotId}`);
      toast.success('Snapshot deletado');
      fetchSnapshots();
    } catch (error) {
      toast.error('Erro ao deletar snapshot');
    }
  };

  const fetchChildSchedule = async (child) => {
    setScheduleLoading(true);
    try {
      const [enrollmentsRes, slotInfoRes] = await Promise.all([
        axios.get(`${API}/enrollments?child_id=${child.id}`),
        axios.get(`${API}/children/${child.id}/slot-info`)
      ]);
      
      const schedule = {};
      daysOfWeek.forEach(d => {
        schedule[d.value] = { 1: null, 2: null, 3: null };
      });
      
      const enrollments = enrollmentsRes.data.filter(e => e.child_id === child.id);
      for (const enrollment of enrollments) {
        const activity = activities.find(a => a.id === enrollment.activity_id);
        for (const [day, slot] of Object.entries(enrollment.time_slots || {})) {
          if (schedule[day]) {
            schedule[day][slot] = {
              activity_id: enrollment.activity_id,
              activity_name: activity?.name || 'N/A',
              enrollment_id: enrollment.id
            };
          }
        }
      }
      
      setChildSchedule({
        child,
        schedule,
        slotInfo: slotInfoRes.data
      });
    } catch (error) {
      toast.error('Erro ao carregar grade da criança');
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchSlotOptions = async (child, day, slot) => {
    setSlotOptionsLoading(true);
    try {
      const response = await axios.get(`${API}/enrollments/slot-options/${child.id}/${day}/${slot}`);
      setSlotOptions(response.data);
      setSelectedSlot({ day, slot });
    } catch (error) {
      toast.error('Erro ao carregar opções');
    } finally {
      setSlotOptionsLoading(false);
    }
  };

  const handleSlotClick = (child, day, slot, currentActivity) => {
    fetchSlotOptions(child, day, slot);
  };

  const handleSelectSlotOption = async (option) => {
    if (!selectedChild || !selectedSlot || !slotOptions) return;
    
    const currentCell = childSchedule?.schedule[selectedSlot.day]?.[selectedSlot.slot];
    
    try {
      if (currentCell) {
        // Mover de uma atividade para outra
        const response = await axios.post(`${API}/enrollments/move-child`, {
          child_id: selectedChild.id,
          from_activity_id: currentCell.activity_id,
          from_day: selectedSlot.day,
          from_slot: selectedSlot.slot,
          to_activity_id: option.activity_id,
          to_day: selectedSlot.day,
          to_slot: selectedSlot.slot,
          force_exceed: false
        });
        
        if (response.data.requires_confirmation) {
          // Turma cheia, pedir confirmação
          setConfirmExceedDialog({
            ...response.data,
            option,
            currentCell
          });
          return;
        }
        
        toast.success(`Movido para ${option.activity_name}`);
      } else {
        // Criar nova matrícula usando endpoint específico
        await axios.post(`${API}/enrollments/add-to-slot`, {
          child_id: selectedChild.id,
          activity_id: option.activity_id,
          day: selectedSlot.day,
          slot: selectedSlot.slot
        });
        toast.success(`Matriculado em ${option.activity_name}`);
      }
      
      // Recarregar dados e atualizar slot info
      setSlotOptions(null);
      setSelectedSlot(null);
      updateChildSlotInfo(selectedChild.id);
      fetchChildSchedule(selectedChild);
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar matrícula');
    }
  };

  const handleConfirmExceed = async () => {
    if (!confirmExceedDialog || !selectedChild || !selectedSlot) return;
    
    try {
      await axios.post(`${API}/enrollments/move-child`, {
        child_id: selectedChild.id,
        from_activity_id: confirmExceedDialog.currentCell.activity_id,
        from_day: selectedSlot.day,
        from_slot: selectedSlot.slot,
        to_activity_id: confirmExceedDialog.option.activity_id,
        to_day: selectedSlot.day,
        to_slot: selectedSlot.slot,
        force_exceed: true
      });
      
      toast.success(`Movido para ${confirmExceedDialog.option.activity_name} (limite excedido)`);
      setConfirmExceedDialog(null);
      setSlotOptions(null);
      setSelectedSlot(null);
      updateChildSlotInfo(selectedChild.id);
      fetchChildSchedule(selectedChild);
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover');
    }
  };

  const handleChildClick = (child, turma) => {
    setSelectedChild({ ...child, turma });
    fetchChildSchedule(child);
  };

  const toggleTurma = (turmaKey) => {
    setExpandedTurmas(prev => ({
      ...prev,
      [turmaKey]: !prev[turmaKey]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    filteredTurmas.forEach(turma => {
      const key = `${turma.activity_id}_${turma.day}_${turma.shift}_${turma.time_slot}`;
      expanded[key] = true;
    });
    setExpandedTurmas(expanded);
  };

  const collapseAll = () => {
    setExpandedTurmas({});
  };

  // Obter atividades disponíveis para o mesmo dia/turno (para transferência) com lotação
  const getTransferOptions = (turma) => {
    return activities
      .filter(act => {
        if (act.id === turma.activity_id) return false;
        const schedule = act.schedule;
        if (schedule && schedule[turma.day]) {
          return schedule[turma.day].includes(turma.shift);
        }
        const actDays = act.days_of_week || [];
        return actDays.includes(turma.day);
      })
      .map(act => {
        // Buscar a turma correspondente para obter lotação e idades
        const matchingTurma = turmas.find(
          t => t.activity_id === act.id && t.day === turma.day && t.shift === turma.shift && t.time_slot === turma.time_slot
        );
        const count = matchingTurma?.total_children || 0;
        const maxStudents = act.max_students || 20;
        // Contar faixas etárias das crianças
        const children = matchingTurma?.children || [];
        const ages = { '6-8': 0, '9-11': 0, '12+': 0 };
        children.forEach(c => {
          const age = c.age || 0;
          if (age <= 8) ages['6-8']++;
          else if (age <= 11) ages['9-11']++;
          else ages['12+']++;
        });
        return { ...act, count, maxStudents, isFull: count >= maxStudents, ages };
      });
  };

  const [transferOptions, setTransferOptions] = useState([]);

  const openTransfer = (turmaKey, turma) => {
    setTransferringTurma(turmaKey);
    setTransferOptions(getTransferOptions(turma));
  };

  const handleTransferGroup = async (turma, toActivityId) => {
    const toAct = transferOptions.find(a => a.id === toActivityId);
    if (!toAct) return;
    if (!confirm(`Transferir TODAS as crianças de "${turma.activity_name}" para "${toAct.name}" neste horário?`)) {
      setTransferringTurma(null);
      return;
    }
    try {
      const response = await axios.post(`${API}/enrollments/transfer-group`, {
        from_activity_id: turma.activity_id,
        to_activity_id: toActivityId,
        day: turma.day,
        slot: turma.time_slot,
        shift: turma.shift
      });
      toast.success(response.data.message);
      setTransferringTurma(null);
      await fetchTurmas();
      // Atualizar slot info de todas as crianças envolvidas
      if (turma.children) {
        turma.children.forEach(child => updateChildSlotInfo(child.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na transferência');
    }
  };

  const filteredTurmas = turmas.filter(turma => {
    if (filters.activity && turma.activity_name !== filters.activity) return false;
    if (filters.day && turma.day !== filters.day) return false;
    if (filters.shift && turma.shift !== filters.shift) return false;
    if (filters.timeSlot && turma.time_slot !== parseInt(filters.timeSlot)) return false;
    return true;
  });

  const getDayLabel = (day) => {
    const found = daysOfWeek.find(d => d.value === day);
    return found ? found.label : day;
  };

  const getTimeSlotLabel = (slot) => {
    const found = timeSlots.find(t => t.value === slot);
    return found ? found.label : `${slot}° Horário`;
  };

  const getTimeSlotHours = (slot, shift) => {
    if (shift === 'Matutino') {
      switch(slot) {
        case 1: return '8h45 - 9h30';
        case 2: return '9h30 - 10h15';
        case 3: return '10h15 - 11h00';
        default: return '';
      }
    } else {
      switch(slot) {
        case 1: return '14h00 - 14h45';
        case 2: return '14h45 - 15h30';
        case 3: return '15h30 - 16h15';
        default: return '';
      }
    }
  };

  // ==================== DRAG & DROP ====================
  
  const handleDragStart = (e, child, turma) => {
    setDraggedChild(child);
    setDragSourceTurma(turma);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', child.id);
  };

  const handleDragOver = (e, turma) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragSourceTurma && 
        turma.day === dragSourceTurma.day && 
        turma.shift === dragSourceTurma.shift &&
        (turma.activity_id !== dragSourceTurma.activity_id || turma.time_slot !== dragSourceTurma.time_slot)) {
      setDropTargetTurma(turma);
    }
  };

  const handleDragLeave = () => {
    setDropTargetTurma(null);
  };

  const handleDrop = async (e, targetTurma) => {
    e.preventDefault();
    
    if (!draggedChild || !dragSourceTurma) return;
    
    if (targetTurma.day !== dragSourceTurma.day || targetTurma.shift !== dragSourceTurma.shift) {
      toast.error('Só é possível mover crianças para turmas do mesmo dia e turno');
      resetDragState();
      return;
    }

    try {
      const response = await axios.post(`${API}/enrollments/move-child`, {
        child_id: draggedChild.id,
        from_activity_id: dragSourceTurma.activity_id,
        from_day: dragSourceTurma.day,
        from_slot: dragSourceTurma.time_slot,
        to_activity_id: targetTurma.activity_id,
        to_day: targetTurma.day,
        to_slot: targetTurma.time_slot,
        force_exceed: false
      });

      if (response.data.requires_confirmation) {
        // Turma cheia, pedir confirmação
        setConfirmExceedDialog({
          ...response.data,
          draggedChild,
          dragSourceTurma,
          targetTurma
        });
        resetDragState();
        return;
      }

      toast.success(`${draggedChild.name} movido para ${targetTurma.activity_name}`);
      // Atualizar slot info da criança movida
      updateChildSlotInfo(draggedChild.id);
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover criança');
    }

    resetDragState();
  };

  const handleConfirmExceedDrag = async () => {
    if (!confirmExceedDialog) return;
    
    const { draggedChild, dragSourceTurma, targetTurma } = confirmExceedDialog;
    
    try {
      await axios.post(`${API}/enrollments/move-child`, {
        child_id: draggedChild.id,
        from_activity_id: dragSourceTurma.activity_id,
        from_day: dragSourceTurma.day,
        from_slot: dragSourceTurma.time_slot,
        to_activity_id: targetTurma.activity_id,
        to_day: targetTurma.day,
        to_slot: targetTurma.time_slot,
        force_exceed: true
      });

      toast.success(`${draggedChild.name} movido para ${targetTurma.activity_name} (limite excedido)`);
      // Atualizar slot info da criança movida
      updateChildSlotInfo(draggedChild.id);
      setConfirmExceedDialog(null);
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover');
    }
  };

  const resetDragState = () => {
    setDraggedChild(null);
    setDragSourceTurma(null);
    setDropTargetTurma(null);
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  // ==================== TRILHA ====================

  const fetchTrilha = async (turma) => {
    setTrilhaLoading(true);
    setTrilhaModalOpen(true);
    try {
      const response = await axios.get(
        `${API}/activities/${turma.activity_id}/turmas/${turma.day}/${turma.shift}/${turma.time_slot}/trilha`
      );
      setTrilhaData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar trilha');
      setTrilhaModalOpen(false);
    } finally {
      setTrilhaLoading(false);
    }
  };

  const exportTrilhaPDF = () => {
    if (!trilhaData || !trilhaData.children?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let startY = addPdfHeader(doc);

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(`Trilha - ${trilhaData.activity_name}`, pageWidth / 2, startY, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(`${trilhaData.day_label} - ${trilhaData.shift_label} - ${trilhaData.slot_label}`, pageWidth / 2, startY + 7, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Total: ${trilhaData.total_children} criancas`, pageWidth / 2, startY + 13, { align: 'center' });

    const removeAccents = (str) => {
      if (!str) return '';
      return String(str)
        .replace(/[ãâáàä]/gi, 'a')
        .replace(/[êéèë]/gi, 'e')
        .replace(/[íìïî]/gi, 'i')
        .replace(/[õôóòö]/gi, 'o')
        .replace(/[úùüû]/gi, 'u')
        .replace(/[ç]/gi, 'c')
        .replace(/[ñ]/gi, 'n');
    };

    const maxActivities = Math.max(...trilhaData.children.map(c => c.trilha?.length || 0), 3);
    
    const headers = ['Crianca'];
    for (let i = 1; i <= maxActivities; i++) {
      headers.push(`${i}o Horario`);
    }

    const rows = trilhaData.children.map(child => {
      const row = [removeAccents(child.name)];
      for (let i = 0; i < maxActivities; i++) {
        const act = child.trilha?.[i];
        row.push(act ? removeAccents(act.activity_name) : '-');
      }
      return row;
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: startY + 18,
      margin: { left: margin, right: margin, top: getPdfHeaderHeight(doc) },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [216, 122, 78], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [254, 243, 239] },
      columnStyles: { 0: { cellWidth: 60 } },
      didDrawPage: (data) => { if (data.pageNumber > 1) addPdfHeader(doc); }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Gerado em ${new Date().toLocaleDateString('pt-BR')} - Pagina ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const fileName = `trilha_${removeAccents(trilhaData.activity_name)}_${trilhaData.day}_${trilhaData.shift}.pdf`;
    doc.save(fileName.replace(/\s+/g, '_').toLowerCase());
    toast.success('PDF exportado com sucesso!');
  };

  // ==================== PDF EXPORT ====================

  const exportTurmaToPDF = (turma) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      let yPos = addPdfHeader(doc);
      
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(turma.activity_name, 14, yPos);
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const shiftLabel = turma.shift === 'Matutino' ? 'Manhã' : 'Tarde';
      const horario = getTimeSlotHours(turma.time_slot, turma.shift);
      doc.text(`${getDayLabel(turma.day)} - ${shiftLabel} - ${getTimeSlotLabel(turma.time_slot)} (${horario})`, 14, yPos + 8);
      doc.text(`Total de crianças: ${turma.total_children}`, 14, yPos + 15);
      
      const tableData = turma.children.map((child, index) => [
        index + 1,
        child.name,
        `${child.age} anos`
      ]);
      
      autoTable(doc, {
        startY: yPos + 22,
        head: [['#', 'Nome', 'Idade']],
        body: tableData,
        theme: 'grid',
        margin: { top: getPdfHeaderHeight(doc) },
        headStyles: { 
          fillColor: [216, 122, 78],
          textColor: 255,
          fontStyle: 'bold'
        },
        didDrawPage: (data) => { if (data.pageNumber > 1) addPdfHeader(doc); }
      });
      
      const fileName = `Turma_${turma.activity_name}_${getDayLabel(turma.day)}_${getTimeSlotLabel(turma.time_slot)}.pdf`
        .replace(/\s+/g, '_')
        .replace(/[°]/g, '');
      doc.save(fileName);
      
      toast.success('PDF exportado!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  // Exportar TODOS os grupos filtrados em um único PDF
  const exportAllFilteredToPDF = () => {
    if (filteredTurmas.length === 0) {
      toast.error('Nenhum grupo para exportar. Aplique os filtros desejados.');
      return;
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = addPdfHeader(doc);
      let isFirstPage = true;

      // Título geral
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Grupos', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      // Informações dos filtros aplicados
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const filtrosTexto = [];
      if (filters.activity) {
        const actName = activities.find(a => a.id === filters.activity)?.name || filters.activity;
        filtrosTexto.push(`Atividade: ${actName}`);
      }
      if (filters.day) filtrosTexto.push(`Dia: ${getDayLabel(filters.day)}`);
      if (filters.shift) filtrosTexto.push(`Turno: ${filters.shift === 'Matutino' ? 'Manhã' : 'Tarde'}`);
      if (filters.timeSlot) filtrosTexto.push(`Horário: ${getTimeSlotLabel(parseInt(filters.timeSlot))}`);
      
      if (filtrosTexto.length > 0) {
        doc.text(`Filtros: ${filtrosTexto.join(' | ')}`, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      }
      
      doc.text(`Total de grupos: ${filteredTurmas.length} | Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      // Iterar sobre cada turma filtrada
      filteredTurmas.forEach((turma, turmaIndex) => {
        // Verificar se precisa de nova página
        const estimatedHeight = 50 + (turma.children?.length || 0) * 7;
        if (yPos + estimatedHeight > pageHeight - 20) {
          doc.addPage();
          yPos = addPdfHeader(doc);
          isFirstPage = false;
        }

        // Cabeçalho da turma
        doc.setFillColor(216, 122, 78);
        doc.rect(14, yPos, pageWidth - 28, 10, 'F');
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        
        const shiftLabel = turma.shift === 'Matutino' ? 'Manhã' : 'Tarde';
        const horario = getTimeSlotHours(turma.time_slot, turma.shift);
        const turmaTitle = `${turma.activity_name} | ${getDayLabel(turma.day)} - ${shiftLabel} - ${getTimeSlotLabel(turma.time_slot)} (${horario})`;
        doc.text(turmaTitle, 16, yPos + 7);
        
        doc.setTextColor(0, 0, 0);
        yPos += 14;

        // Informação de quantidade
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${turma.total_children} criança(s) | Faixa etária predominante: ${turma.age_group_label || 'N/A'}`, 16, yPos);
        yPos += 6;

        // Lista de crianças
        if (turma.children && turma.children.length > 0) {
          const tableData = turma.children.map((child, index) => [
            index + 1,
            child.name,
            `${child.age} anos`
          ]);
          
          autoTable(doc, {
            startY: yPos,
            head: [['#', 'Nome', 'Idade']],
            body: tableData,
            theme: 'striped',
            margin: { left: 14, right: 14 },
            headStyles: { 
              fillColor: [100, 100, 100],
              textColor: 255,
              fontSize: 8
            },
            bodyStyles: {
              fontSize: 8
            },
            columnStyles: {
              0: { cellWidth: 10 },
              1: { cellWidth: 'auto' },
              2: { cellWidth: 25 }
            }
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
        } else {
          doc.setFontSize(9);
          doc.setTextColor(150, 150, 150);
          doc.text('Nenhuma criança matriculada', 16, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 10;
        }
      });

      // Rodapé em todas as páginas
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${i} de ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      const fileName = `Grupos_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success(`PDF exportado com ${filteredTurmas.length} grupo(s)!`);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleGenerateGrades = async () => {
    // Pegar todas as crianças ativas (não apenas as filtradas)
    const confirmed = window.confirm(
      `Esta ação vai gerar grades para TODAS as crianças que possuem atividades escolhidas.\n\n` +
      `As matrículas existentes dessas crianças serão substituídas.\n\n` +
      `Regras aplicadas:\n` +
      `• Todas as atividades escolhidas constarão na grade\n` +
      `• "Integração" pode repetir várias vezes, mas apenas na Segunda-feira\n` +
      `• Outras atividades podem repetir no máximo 2x na grade\n` +
      `• Nenhuma atividade repete no mesmo dia\n\n` +
      `Deseja continuar?`
    );
    
    if (!confirmed) return;
    
    setGeneratingGrades(true);
    try {
      const response = await axios.post(`${API}/enrollments/generate-grades`, {
        clear_existing: true
      });
      
      const result = response.data;
      setGradeResultDialog(result);
      
      toast.success(`Grades geradas! ${result.summary?.enrollments_created || 0} matrículas criadas.`);
      
      // Recarregar turmas
      fetchTurmas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar grades');
    } finally {
      setGeneratingGrades(false);
    }
  };

  const handleSyncActivities = async () => {
    setSyncingActivities(true);
    try {
      const response = await axios.post(`${API}/children/sync-selected-activities`);
      const result = response.data;
      toast.success(`Sincronizado! ${result.children_updated} crianças atualizadas.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setSyncingActivities(false);
    }
  };

  const totalChildren = filteredTurmas.reduce((acc, turma) => acc + turma.total_children, 0);

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Turmas</h1>
          <p className="text-slate-600 mt-1">
            {canEditGroups ? 'Arraste crianças entre turmas • Clique para ver a grade' : 'Visualização das turmas'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Botões de edição - apenas com permissão */}
          {canEditGroups && (
          <>
          <Button 
            onClick={() => {
              fetchSnapshots();
              setSaveSnapshotDialogOpen(true);
            }}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="save-snapshot-button"
            title="Salvar estado atual dos grupos"
          >
            <Save className="h-4 w-4 mr-2" />
            Salvar Versão
          </Button>
          <Button 
            onClick={() => {
              fetchSnapshots();
              setSnapshotsDialogOpen(true);
            }}
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
            data-testid="snapshots-button"
            title="Ver versões salvas dos grupos"
          >
            <History className="h-4 w-4 mr-2" />
            Versões
          </Button>
          <Button 
            onClick={handleSyncActivities}
            disabled={syncingActivities}
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            data-testid="sync-activities-button"
            title="Sincronizar atividades escolhidas com base nas matrículas existentes"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncingActivities ? 'animate-spin' : ''}`} />
            {syncingActivities ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
          <Button 
            onClick={handleGenerateGrades}
            disabled={generatingGrades}
            className="bg-green-600 hover:bg-green-700"
            data-testid="generate-grades-button"
            title="Gerar grades automaticamente baseado nas atividades escolhidas de cada criança"
          >
            <Wand2 className={`h-4 w-4 mr-2 ${generatingGrades ? 'animate-spin' : ''}`} />
            {generatingGrades ? 'Gerando...' : 'Gerar Grades'}
          </Button>
          </>
          )}
          <Button onClick={exportAllFilteredToPDF} className="bg-[#D87A4E] hover:bg-[#c26a42]" data-testid="export-all-groups-pdf">
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Filtros</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Atividade</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.activity}
              onChange={(e) => setFilters({...filters, activity: e.target.value})}
            >
              <option value="">Todas</option>
              {[...new Set(activities.map(a => a.name))].map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">Dia</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.day}
              onChange={(e) => setFilters({...filters, day: e.target.value})}
            >
              <option value="">Todos</option>
              {daysOfWeek.map(day => (
                <option key={day.value} value={day.value}>{day.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">Turno</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.shift}
              onChange={(e) => setFilters({...filters, shift: e.target.value})}
            >
              <option value="">Todos</option>
              <option value="Matutino">Manhã</option>
              <option value="Vespertino">Tarde</option>
            </select>
          </div>

          <div>
            <Label className="text-xs">Horário</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.timeSlot}
              onChange={(e) => setFilters({...filters, timeSlot: e.target.value})}
            >
              <option value="">Todos</option>
              {timeSlots.map(slot => (
                <option key={slot.value} value={slot.value}>{slot.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <div className="text-sm text-slate-600">
            {filteredTurmas.length} turma(s) • {totalChildren} matrícula(s)
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={expandAll}>
              <ChevronDown className="h-4 w-4 mr-1" /> Expandir
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAll}>
              <ChevronUp className="h-4 w-4 mr-1" /> Recolher
            </Button>
            <Button variant="outline" size="sm" onClick={() => setFilters({ activity: '', day: '', shift: '', timeSlot: '' })}>
              Limpar
            </Button>
          </div>
        </div>
      </div>

      {/* Lista de Turmas */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D87A4E]"></div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="turmas-list">
          {filteredTurmas.map((turma) => {
            const turmaKey = `${turma.activity_id}_${turma.day}_${turma.shift}_${turma.time_slot}`;
            const isExpanded = expandedTurmas[turmaKey];
            const isDropTarget = dropTargetTurma && 
              dropTargetTurma.activity_id === turma.activity_id && 
              dropTargetTurma.day === turma.day &&
              dropTargetTurma.time_slot === turma.time_slot;
            
            const activity = activities.find(a => a.id === turma.activity_id);
            const maxStudents = activity?.max_students || 20;
            const isFull = turma.total_children >= maxStudents;
            
            return (
              <div
                key={turmaKey}
                className={`bg-white rounded-xl shadow-md border overflow-hidden transition-all ${
                  isDropTarget ? 'border-[#D87A4E] border-2 ring-2 ring-[#D87A4E]/30' : 
                  isFull ? 'border-amber-300' : 'border-slate-100'
                }`}
                onDragOver={(e) => handleDragOver(e, turma)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, turma)}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleTurma(turmaKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isFull ? 'bg-amber-100' : 'bg-[#D87A4E]/20'
                      }`}>
                        <UsersRound className={`h-6 w-6 ${isFull ? 'text-amber-600' : 'text-[#D87A4E]'}`} />
                      </div>
                      <div>
                        <div className="relative">
                          <h3
                            className={`font-semibold text-lg transition-colors inline-flex items-center gap-1.5 ${
                              canEditGroups
                                ? 'text-slate-800 hover:text-indigo-600 cursor-pointer group'
                                : 'text-slate-800'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canEditGroups) {
                                if (transferringTurma === turmaKey) setTransferringTurma(null);
                                else openTransfer(turmaKey, turma);
                              }
                            }}
                            title={canEditGroups ? "Clique para transferir grupo" : turma.activity_name}
                          >
                            {turma.activity_name}
                            {canEditGroups && (
                              <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                              </svg>
                            )}
                          </h3>
                          {transferringTurma === turmaKey && (
                            <div
                              className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[280px] max-h-[300px] overflow-y-auto"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
                                <p className="text-xs text-slate-500 font-medium">Transferir para:</p>
                              </div>
                              {transferOptions.length === 0 ? (
                                <div className="px-3 py-4 text-sm text-slate-400 text-center">
                                  Nenhuma atividade disponível neste dia/turno
                                </div>
                              ) : (
                                transferOptions.map(opt => (
                                  <button
                                    key={opt.id}
                                    className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors border-b border-slate-50 last:border-b-0"
                                    onClick={() => handleTransferGroup(turma, opt.id)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium text-sm text-slate-800">{opt.name}</span>
                                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                        opt.isFull ? 'bg-amber-100 text-amber-700' : opt.count === 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                                      }`}>
                                        {opt.count}/{opt.maxStudents}
                                      </span>
                                    </div>
                                    {opt.count > 0 && (
                                      <div className="flex gap-2 mt-1">
                                        {opt.ages['6-8'] > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">6-8: {opt.ages['6-8']}</span>}
                                        {opt.ages['9-11'] > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">9-11: {opt.ages['9-11']}</span>}
                                        {opt.ages['12+'] > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded">12+: {opt.ages['12+']}</span>}
                                      </div>
                                    )}
                                    {opt.count === 0 && (
                                      <p className="text-[10px] text-green-500 mt-0.5">Vazia</p>
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                            {getDayLabel(turma.day)}
                          </span>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                            {turma.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                          </span>
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                            {getTimeSlotLabel(turma.time_slot)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); fetchTrilha(turma); }} title="Ver Trilha">
                        <Route className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); exportTurmaToPDF(turma); }} title="Exportar PDF">
                        <FileText className="h-4 w-4" />
                      </Button>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                        isFull ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {turma.total_children}/{maxStudents}
                      </span>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100">
                    <div className="p-4 bg-slate-50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {turma.children.map((child, index) => {
                          const slotInfo = childrenSlotInfo[child.id];
                          const hasVacantSlots = slotInfo && slotInfo.vacant_slots > 0;
                          
                          return (
                          <div
                            key={child.id}
                            draggable={canEditGroups}
                            onDragStart={canEditGroups ? (e) => handleDragStart(e, child, turma) : undefined}
                            onDragEnd={canEditGroups ? handleDragEnd : undefined}
                            onClick={() => handleChildClick(child, turma)}
                            className={`flex items-center gap-3 bg-white p-3 rounded-lg border cursor-pointer hover:border-[#D87A4E] hover:shadow-md transition-all group ${
                              draggedChild?.id === child.id ? 'opacity-50' : ''
                            } ${hasVacantSlots ? 'border-amber-300' : 'border-slate-200'}`}
                          >
                            {canEditGroups && <GripVertical className="h-4 w-4 text-slate-300 group-hover:text-[#D87A4E] cursor-grab" />}
                            <span className="text-xs text-slate-400 font-mono w-5">{index + 1}.</span>
                            <div className="relative">
                              <ChildAvatar photo={child.photo_url} name={child.name} size="md" zoomable={true} className={`border-2 ${hasVacantSlots ? 'border-amber-400' : 'border-[#D87A4E]/30'}`} />
                              {/* Indicador de presença do dia */}
                              {child.attendance_status && (
                                <div 
                                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                                    child.attendance_status === 'present' ? 'bg-green-500' :
                                    child.attendance_status === 'justified' ? 'bg-orange-500' :
                                    'bg-red-500'
                                  }`}
                                  title={
                                    child.attendance_status === 'present' ? 'Presente hoje' :
                                    child.attendance_status === 'justified' ? 'Falta justificada' :
                                    'Ausente hoje'
                                  }
                                />
                              )}
                              {hasVacantSlots && (
                                <div 
                                  className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center"
                                  title={`${slotInfo.vacant_slots} slot(s) vazio(s)`}
                                >
                                  <span className="text-white text-[10px] font-bold">{slotInfo.vacant_slots}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 text-sm truncate">{child.name}</p>
                              <p className="text-xs text-slate-500">
                                {child.age} anos
                                {hasVacantSlots && (
                                  <span className="text-amber-600 ml-1">• {slotInfo.filled_slots}/{slotInfo.total_slots}</span>
                                )}
                              </p>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && filteredTurmas.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <UsersRound className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhuma turma encontrada</p>
        </div>
      )}

      {/* Modal de Grade da Criança */}
      <Dialog open={!!selectedChild} onOpenChange={() => { setSelectedChild(null); setSlotOptions(null); setSelectedSlot(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedChild && (
                <>
                  <ChildAvatar photo={selectedChild.photo_url} name={selectedChild.name} size="lg" zoomable={true} />
                  <div>
                    <span>{selectedChild.name}</span>
                    <p className="text-sm font-normal text-slate-500">{selectedChild.age} anos</p>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {scheduleLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D87A4E]"></div>
            </div>
          ) : childSchedule ? (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                  {childSchedule.slotInfo?.filled_slots || 0} slots preenchidos
                </span>
                {childSchedule.slotInfo?.vacant_slots > 0 && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full">
                    {childSchedule.slotInfo.vacant_slots} slots vazios
                  </span>
                )}
              </div>
              
              <p className="text-sm text-slate-600">
                Clique em um slot para ver opções de atividades disponíveis
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-slate-200 bg-slate-100 p-2 text-xs font-medium text-slate-600">Horário</th>
                      {daysOfWeek.map(day => (
                        <th key={day.value} className="border border-slate-200 bg-slate-100 p-2 text-xs font-medium text-slate-600">
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map(slot => (
                      <tr key={slot}>
                        <td className="border border-slate-200 bg-slate-50 p-2 text-xs font-medium text-slate-600 text-center">
                          {getTimeSlotLabel(slot)}
                        </td>
                        {daysOfWeek.map(day => {
                          const cell = childSchedule.schedule[day.value]?.[slot];
                          // Permitir clicar em TODOS os dias que a criança frequenta
                          const childDays = selectedChild?.days_of_week || childSchedule.slotInfo?.child_days || [];
                          const isChildDay = childDays.includes(day.value);
                          const isSelected = selectedSlot?.day === day.value && selectedSlot?.slot === slot;
                          
                          return (
                            <td 
                              key={day.value} 
                              className={`border border-slate-200 p-2 text-center text-xs cursor-pointer transition-all ${
                                isSelected ? 'ring-2 ring-[#D87A4E] bg-[#D87A4E]/20' :
                                cell ? 'bg-[#D87A4E]/10 hover:bg-[#D87A4E]/20' : 
                                isChildDay ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50'
                              }`}
                              onClick={() => isChildDay && handleSlotClick(selectedChild, day.value, slot, cell)}
                            >
                              {cell ? (
                                <span className="font-medium text-[#D87A4E]">{cell.activity_name}</span>
                              ) : isChildDay ? (
                                <span className="text-amber-500">vazio</span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Opções de atividades para o slot selecionado */}
              {slotOptionsLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D87A4E]"></div>
                </div>
              )}
              
              {slotOptions && selectedSlot && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h4 className="font-medium text-slate-800 mb-2">
                    Opções para {getDayLabel(selectedSlot.day)} - {getTimeSlotLabel(selectedSlot.slot)}
                  </h4>
                  {slotOptions.child_age && (
                    <p className="text-xs text-slate-500 mb-3">
                      {slotOptions.child_name} ({slotOptions.child_age} anos - {slotOptions.child_age_group})
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {slotOptions.options.map(option => (
                      <button
                        key={option.activity_id}
                        onClick={() => handleSelectSlotOption(option)}
                        className={`text-left p-3 rounded-lg border transition-all ${
                          option.is_full 
                            ? 'bg-amber-50 border-amber-200 hover:border-amber-400' 
                            : 'bg-white border-slate-200 hover:border-[#D87A4E]'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-slate-800">{option.activity_name}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            option.is_full ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {option.current_count}/{option.max_students}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-slate-500">
                            {option.activity_type} • {option.available > 0 ? `${option.available} vagas` : 'Turma cheia'}
                          </p>
                          {option.age_group && option.current_count > 0 && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              option.age_group === 'Faixa etária mista' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {option.age_group}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmação para exceder limite */}
      <AlertDialog open={!!confirmExceedDialog} onOpenChange={() => setConfirmExceedDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Turma Cheia
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmExceedDialog?.message || 'A turma destino já atingiu a capacidade máxima.'}
              <br /><br />
              Deseja exceder o limite de alunos?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmExceedDialog?.draggedChild ? handleConfirmExceedDrag : handleConfirmExceed}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Sim, exceder limite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Resultado da Geração de Grades */}
      <Dialog open={!!gradeResultDialog} onOpenChange={() => setGradeResultDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resultado da Geração de Grades</DialogTitle>
          </DialogHeader>
          {gradeResultDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{gradeResultDialog.summary?.children_processed || 0}</p>
                  <p className="text-xs text-green-700">Crianças Processadas</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{gradeResultDialog.summary?.enrollments_created || 0}</p>
                  <p className="text-xs text-blue-700">Matrículas Criadas</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{gradeResultDialog.summary?.children_without_activities || 0}</p>
                  <p className="text-xs text-amber-700">Sem Atividades</p>
                </div>
              </div>
              
              {gradeResultDialog.summary?.flexible_age_used > 0 && (
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-sm text-purple-700">
                    <strong>{gradeResultDialog.summary.flexible_age_used}</strong> matrículas usaram faixa etária flexível
                  </p>
                </div>
              )}
              
              {gradeResultDialog.summary?.activity_repeated > 0 && (
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-sm text-orange-700">
                    <strong>{gradeResultDialog.summary.activity_repeated}</strong> atividades foram repetidas para preencher slots
                  </p>
                </div>
              )}
              
              {gradeResultDialog.summary?.integration_monday > 0 && (
                <div className="bg-cyan-50 p-3 rounded-lg">
                  <p className="text-sm text-cyan-700">
                    <strong>{gradeResultDialog.summary.integration_monday}</strong> matrículas de Integração na Segunda-feira
                  </p>
                </div>
              )}
              
              {gradeResultDialog.summary?.slots_unfilled > 0 && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>{gradeResultDialog.summary.slots_unfilled}</strong> slots não puderam ser preenchidos
                  </p>
                </div>
              )}
              
              {gradeResultDialog.details && gradeResultDialog.details.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Detalhes (primeiras {gradeResultDialog.details.length} crianças):</h4>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {gradeResultDialog.details.map((detail, index) => (
                      <div key={index} className="text-sm bg-slate-50 p-2 rounded">
                        <p className="font-medium">{detail.child_name}</p>
                        <p className="text-slate-600">{detail.enrollments?.length || 0} matrículas</p>
                        {detail.flexible_age_used?.length > 0 && (
                          <p className="text-purple-600 text-xs">Faixa flexível: {detail.flexible_age_used.join(', ')}</p>
                        )}
                        {detail.repeated_activities?.length > 0 && (
                          <p className="text-orange-600 text-xs">Repetidas: {detail.repeated_activities.join(', ')}</p>
                        )}
                        {detail.integration_monday?.length > 0 && (
                          <p className="text-cyan-600 text-xs">Integração (Seg): {detail.integration_monday.join(', ')}</p>
                        )}
                        {detail.unfilled_slots?.length > 0 && (
                          <p className="text-red-600 text-xs">Não preenchidos: {detail.unfilled_slots.join(', ')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setGradeResultDialog(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para Salvar Snapshot */}
      <Dialog open={saveSnapshotDialogOpen} onOpenChange={setSaveSnapshotDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-blue-500" />
              Salvar Versão dos Grupos
            </DialogTitle>
            <DialogDescription>
              Salve o estado atual da organização das turmas para poder restaurar posteriormente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="snapshot-name">Nome da versão *</Label>
              <Input
                id="snapshot-name"
                value={snapshotName}
                onChange={(e) => setSnapshotName(e.target.value)}
                placeholder="Ex: Distribuição inicial 2025"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="snapshot-description">Descrição (opcional)</Label>
              <Input
                id="snapshot-description"
                value={snapshotDescription}
                onChange={(e) => setSnapshotDescription(e.target.value)}
                placeholder="Ex: Antes de reorganizar por faixa etária"
                className="mt-1"
              />
            </div>
            
            {snapshots.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Versões existentes: {snapshots.length}</p>
                <div className="text-xs text-slate-400">
                  Última: {snapshots[0]?.name} ({new Date(snapshots[0]?.created_at).toLocaleDateString('pt-BR')})
                </div>
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSaveSnapshotDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveSnapshot} 
                disabled={savingSnapshot || !snapshotName.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingSnapshot ? 'Salvando...' : 'Salvar Versão'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para Listar e Restaurar Snapshots */}
      <Dialog open={snapshotsDialogOpen} onOpenChange={setSnapshotsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-500" />
              Versões Salvas dos Grupos
            </DialogTitle>
            <DialogDescription>
              Selecione uma versão para restaurar o estado anterior dos grupos.
            </DialogDescription>
          </DialogHeader>
          
          {snapshots.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma versão salva ainda.</p>
              <p className="text-sm">Clique em "Salvar Versão" para criar um checkpoint.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((snapshot) => (
                <div 
                  key={snapshot.id}
                  className={`p-4 border rounded-lg ${
                    snapshot.is_auto_backup 
                      ? 'bg-amber-50 border-amber-200' 
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        {snapshot.name}
                        {snapshot.is_auto_backup && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            Auto-backup
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-slate-500 mt-1">
                        {new Date(snapshot.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {' • '}{snapshot.enrollments_count} matrículas
                        {' • '}{snapshot.children_count} crianças
                      </p>
                      {snapshot.description && (
                        <p className="text-xs text-slate-400 mt-1">{snapshot.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                        onClick={() => setConfirmRestoreDialog(snapshot)}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Restaurar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteSnapshot(snapshot.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmação de Restauração */}
      <AlertDialog open={!!confirmRestoreDialog} onOpenChange={() => setConfirmRestoreDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar Restauração
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Você está prestes a restaurar a versão <strong>"{confirmRestoreDialog?.name}"</strong>.
              </p>
              <p className="text-amber-600 font-medium">
                ⚠️ ATENÇÃO: Todas as matrículas atuais serão substituídas!
              </p>
              <p className="text-sm">
                Um backup automático do estado atual será criado antes da restauração.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleRestoreSnapshot(confirmRestoreDialog)}
              disabled={restoringSnapshot}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {restoringSnapshot ? 'Restaurando...' : 'Sim, Restaurar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Trilha */}
      <Dialog open={trilhaModalOpen} onOpenChange={setTrilhaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Route className="w-5 h-5 text-[#D87A4E]" />
                Trilha da Turma
              </div>
              {trilhaData && (
                <Button
                  onClick={exportTrilhaPDF}
                  size="sm"
                  variant="outline"
                  className="ml-4"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {trilhaLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D87A4E]"></div>
            </div>
          ) : trilhaData ? (
            <div className="space-y-4">
              <div className="bg-[#D87A4E]/10 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-800">{trilhaData.activity_name}</h3>
                <p className="text-sm text-slate-600">
                  {trilhaData.day_label} - {trilhaData.shift_label} - {trilhaData.slot_label}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Total: {trilhaData.total_children} criança(s)
                </p>
              </div>

              {trilhaData.children && trilhaData.children.length > 0 ? (
                <div className="space-y-2">
                  {trilhaData.children.map((child, index) => (
                    <div 
                      key={child.id || index}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:shadow-sm transition-shadow"
                    >
                      <ChildAvatar photo={child.photo_url} name={child.name} size="sm" zoomable={true} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{child.name}</p>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 flex-wrap">
                          {child.trilha?.map((act, actIndex) => (
                            <span key={actIndex} className="flex items-center">
                              <span className={`px-2 py-0.5 rounded-full ${
                                act.activity_name === trilhaData.activity_name
                                  ? 'bg-[#D87A4E]/20 text-[#D87A4E] font-medium'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {act.activity_name}
                              </span>
                              {actIndex < child.trilha.length - 1 && (
                                <span className="mx-1 text-slate-400">→</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Nenhuma criança encontrada para esta turma
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Erro ao carregar dados da trilha
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Groups;
